import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../common/Button';
import type { AdminUserView } from '../../types/admin-user.types';

interface DeleteAccountConfirmModalProps {
  account: AdminUserView;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

export function DeleteAccountConfirmModal({
  account,
  onClose,
  onConfirm,
}: DeleteAccountConfirmModalProps): React.ReactElement {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState('');

  const isConfirmed = confirmText.trim().toLowerCase() === 'delete';

  const handleConfirm = async () => {
    setError(null);
    if (!isConfirmed) { setError('Please type "delete" to confirm.'); return; }
    setLoading(true);
    try {
      await onConfirm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Deletion failed.');
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="delete-modal-title">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-sm glass-card p-6 animate-slide-in">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-red-500/20 border border-red-500/30 flex items-center justify-center">
            <svg className="w-5 h-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </div>
          <div>
            <h2 id="delete-modal-title" className="text-lg font-bold text-white">Delete Account</h2>
            <p className="text-xs text-red-400">This action cannot be undone</p>
          </div>
        </div>

        <p className="text-sm text-slate-400 mb-1">
          You are about to permanently delete:
        </p>
        <p className="text-sm font-semibold text-white mb-4">{account.email}</p>
        <p className="text-sm text-slate-400 mb-4">
          All associated data will be deleted — vocabulary ({account.wordCount} words), learning progress, review history, and vocabulary groups.
        </p>

        <div className="mb-4">
          <label htmlFor="delete-confirm-input" className="block text-xs font-medium text-slate-400 mb-1.5">
            Type <span className="font-mono text-red-400">delete</span> to confirm
          </label>
          <input
            id="delete-confirm-input"
            type="text"
            value={confirmText}
            onChange={e => setConfirmText(e.target.value)}
            placeholder="delete"
            className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
          />
        </div>

        {error && (
          <div className="p-3 mb-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
            {error}
          </div>
        )}

        <div className="flex gap-3">
          <Button
            id="delete-modal-cancel-btn"
            type="button"
            variant="ghost"
            className="flex-1"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            id="delete-modal-confirm-btn"
            type="button"
            variant="danger"
            className="flex-1"
            loading={loading}
            disabled={!isConfirmed}
            onClick={() => { void handleConfirm(); }}
          >
            Delete Account
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
